/* eslint-disable max-lines */

import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  signal,
  untracked,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';

import { Store } from '@ngrx/store';
import { combineLatest, distinctUntilChanged, filter, map, of, switchMap, tap } from 'rxjs';
import { startWith } from 'rxjs/operators';

import { Analytics } from '@core/analytics-types';
import {
  BulkActionsController,
  BulkActionsSelectionMessageComponent,
  BulkActionsToolbarComponent,
  type BulkItemBase,
} from '@core/bulk-actions-legacy';
import { filterBoolean, provide } from '@core/common-legacy';
import {
  ConnectedOverlayDirective,
  DropdownComponent,
  GroupComponent,
  IconButtonComponent,
  OverlayOriginDirective,
  WindowRef,
} from '@core/user-interface-legacy';
import { injectUntilDestroyed } from '@core/utils-legacy';
import { ViewActions, ViewSelectors } from '@core/views-data';
import { DataSourceService, View, ViewFormHook, ViewLayout } from '@core/views-types';
import { dataSource$, dataSourceService$, injectViewDataScope } from '@core/views-util';

import { ViewsAnalytics } from '../../analytics/views-analytics';
import { provideInputService } from '../../filters/advanced-filters/tokens';
import { InputMultiSelectService } from '../../filters/input-types/input-multiselect/input-multiselect.service';
import { InputRangeService } from '../../filters/input-types/input-range/input-range.service';
import { InputSelectService } from '../../filters/input-types/input-select/input-select.service';
import { InputTextService } from '../../filters/input-types/input-text/input-text.service';
import { OptionsService } from '../../filters/input-types/options.service';
import { QuickFiltersContainerComponent } from '../../filters/quick-filters/quick-filters-container/quick-filters-container.component';
import { FiltersDataService } from '../../filters/services/filters-data.service';
import { FiltersFormDataService } from '../../filters/services/filters-form-data.service';
import { FiltersFormStateService } from '../../filters/services/filters-form-state.service';
import { FiltersFormService } from '../../filters/services/filters-form.service';
import { bulkActionsButtonMode, currentViewProvider$, provideCurrentView } from '../consts';
import { ViewDataService } from '../view-data.service';
import { ViewFieldsService } from '../view-fields.service';
import { ViewFormService } from '../view-form.service';
import { ViewLayoutPickerComponent } from '../view-layout-picker/view-layout-picker.component';
import { DefaultSearchComponent } from '../view-page/default-search/default-search.component';
import { ViewsEmptyStateComponent } from '../view-page/empty-state/views-empty-state.component';
import { LayoutBoardComponent } from '../view-page/layouts/layout-board/layout-board.component';
import { LayoutListComponent } from '../view-page/layouts/layout-list/layout-list.component';
import { LayoutSharedService } from '../view-page/layouts/layout-shared.service';
import { LayoutTableComponent } from '../view-page/layouts/layout-table/layout-table.component';
import { ViewPageService } from '../view-page/view-page.service';
import { ViewSortComponent } from '../view-page/view-sort/view-sort.component';
import { ViewSettingsFieldsComponent } from '../view-settings/view-settings-fields/view-settings-fields.component';
import { ViewSettingsGroupsComponent } from '../view-settings/view-settings-groups/view-settings-groups.component';
import { ViewsNotificationsService } from '../views-notifications.service';

type ControlGroupVisibility = Record<
  'sort' | 'groups' | 'fields',
  { visible: boolean; className: string | null }
>;

@Component({
  standalone: true,
  selector: 'rt-simple-view',
  templateUrl: './simple-view.component.html',
  styleUrl: './simple-view.component.less',
  imports: [
    DefaultSearchComponent,
    GroupComponent,
    ViewSortComponent,
    ViewSettingsGroupsComponent,
    ViewsEmptyStateComponent,
    IconButtonComponent,
    DropdownComponent,
    ConnectedOverlayDirective,
    OverlayOriginDirective,
    LayoutListComponent,
    LayoutTableComponent,
    AsyncPipe,
    LayoutBoardComponent,
    QuickFiltersContainerComponent,
    ViewLayoutPickerComponent,
    ReactiveFormsModule,
    ViewSettingsFieldsComponent,
    NgTemplateOutlet,
    BulkActionsSelectionMessageComponent,
    BulkActionsToolbarComponent,
  ],
  providers: [
    provideCurrentView(),
    LayoutSharedService,
    ViewFormService,
    FiltersFormService,
    FiltersFormStateService,
    FiltersDataService,
    FiltersFormDataService,
    ViewFieldsService,
    ViewsAnalytics,
    provide(Analytics, { useExisting: ViewsAnalytics }),
    provideInputService('select', InputSelectService),
    provideInputService('multi_select', InputMultiSelectService),
    provideInputService('text', InputTextService),
    provideInputService('range', InputRangeService),
    OptionsService,
    ViewDataService,
    ViewPageService,
    ViewsNotificationsService,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    '[style.max-height.px]': 'autoSetHeight() ? height() : null',
  },
})
export class SimpleViewComponent {
  private readonly viewDataScope = injectViewDataScope();
  private readonly store = inject(Store);
  private readonly viewDataService = inject(ViewDataService);
  private readonly viewFormService = inject(ViewFormService);
  private readonly untilDestroyed = injectUntilDestroyed();
  private readonly dataSource$ = inject(dataSource$);
  private readonly dataSourceService$ = inject(dataSourceService$);
  private readonly currentViewProvider$ = inject(currentViewProvider$);
  private readonly window = inject(WindowRef);
  private readonly selfElRef = inject(ElementRef<HTMLElement>);
  private readonly injector = inject(Injector);
  private readonly bulkActionsController = inject(BulkActionsController, { optional: true });

  protected readonly bulkActionsButtonMode = bulkActionsButtonMode;

  public viewId = input.required<string>();
  public hasGroupsControl = input(true);
  public hasLayoutPicker = input(false);
  public hasQuickFilters = input(false);
  public hasAdvancedFilters = input(false);
  public viewFormHook = input<ViewFormHook | null>(null);
  public hasControls = input(true);
  public initialLayout = input<ViewLayout['type'] | null>(null);
  public forcedLayout = input<ViewLayout['type'] | null>(null);
  public layouts = input<Array<ViewLayout['type']>>(['list', 'table', 'board']);
  public selfScrollEnabled = input(false);
  public virtualScrollEnabled = input(false);
  public autoSetHeight = input(true);
  public hideEmptyGroups = input<boolean | null>(null);
  public hasFieldsPicker = input<boolean>(false);
  public hasTableWrappers = input(false);
  public hasBulkActions = input(false);
  public refetchOnFieldsChange = input(false);

  public isEmptyStateActive = computed(() => this.isEmpty() && this.isLoaded());
  public isPredefinedView = signal(false);
  public filtersApplied = this.viewFormService.filtersApplied;
  public filtersChanged = computed(() => {
    const cleanView = this.currentViewClean();
    if (!cleanView) return false;

    const _notifier = this.viewFormService.formValue();
    const currentState = this.viewFormService.getValue();
    return untracked(() => this.viewFormService.hasFiltersChanged(cleanView, currentState));
  });

  protected bulkActionsSelectedItems = toSignal(
    this.bulkActionsController?.selectedItems$ ?? of(<BulkItemBase[]>[]),
    { initialValue: [] },
  );
  protected isEmpty = signal(false);
  protected isLoaded = signal(false);
  protected viewsRecordsTotalCount = signal<number | null>(null);
  protected initialViewLoaded = signal(false);
  protected viewForm = this.viewFormService.viewForm;
  protected dataSource = toSignal(this.dataSource$, { requireSync: true });
  protected searchControl = this.viewFormService.viewForm.controls.searchQuery;
  protected groupControl = this.viewFormService.viewForm.controls.group;
  protected layoutControl = this.viewFormService.viewForm.controls.layout;
  protected dataSourceService = toSignal(this.dataSourceService$, { requireSync: true });
  protected configIdValue = computed(() => this.viewFormService.formValue().group?.configId);
  protected viewLayoutType = computed(() => {
    const forcedLayout = this.forcedLayout();
    if (forcedLayout) return forcedLayout;

    const initialLayout = this.initialLayout();
    if (initialLayout && !this.initialViewLoaded()) {
      return initialLayout;
    } else {
      return this.viewFormService.formValue().layout.type ?? 'list';
    }
  });
  protected availableLayouts = computed(() =>
    this.dataSource().layouts.filter(layout => this.layouts().includes(layout.type)),
  );
  protected layoutBoardCssVars = <const>{
    groupsHeaderBgColor: 'colorBgDefault',
    groupWidth: 366,
    groupHeaderPaddingLr: 8,
    groupHeaderBottomMargin: 0,
    groupItemMarginLr: 8,
    groupRadius: 8,
  };
  protected height = signal<number>(0);
  protected controlGroupVisibility = computed(() => {
    const order = <const>['sort', 'groups', 'fields'];
    const visibility = {
      sort: true,
      groups: this.hasGroupsControl(),
      fields: this.hasFieldsPicker(),
    };
    const computedVisibility = Object.fromEntries(
      order
        .filter(key => visibility[key])
        .map((key, i, arr) => [
          key,
          {
            visible: visibility[key],
            className:
              arr.length === 1
                ? 'is-only'
                : i === 0
                  ? 'is-first'
                  : i === arr.length - 1
                    ? 'is-last'
                    : null,
          },
        ]),
    );
    return <ControlGroupVisibility>{
      ...Object.fromEntries(order.map(key => [key, { visible: false, className: null }])),
      ...computedVisibility,
    };
  });

  private mainDropdown = viewChild(DropdownComponent);
  private groupsComponent = viewChild(ViewSettingsGroupsComponent);
  private currentViewClean = signal<View | null>(null);
  private setDropdownTemplate = effect(() => {
    const mainDropdown = this.mainDropdown();
    const groupsComponent = this.groupsComponent();
    if (!mainDropdown || !groupsComponent) return;

    const groupingTmpl = groupsComponent.groupingTmpl();
    untracked(() => {
      mainDropdown.clearTemplates();
      mainDropdown.setNestedTemplate(groupingTmpl);
    });
  });
  private addFormHooks = effect(onCleanup => {
    const viewFormHook = this.viewFormHook();
    if (!viewFormHook) return;

    const destroy = viewFormHook(this.viewForm, this.dataSource());
    onCleanup(() => destroy());
  });
  private watchHideEmptyGroups = toObservable(this.hideEmptyGroups)
    .pipe(
      switchMap(hide => {
        if (hide === null) return [];

        const { hideEmpty: hideEmptyControl } = this.viewForm.controls.group.controls;
        return hideEmptyControl.valueChanges.pipe(
          startWith(hideEmptyControl.value),
          tap(hideEmptyValue => {
            if (hideEmptyValue === hide) return;

            hideEmptyControl.setValue(hide);
          }),
        );
      }),
      this.untilDestroyed,
    )
    .subscribe();
  private currentView$ = this.viewDataService.routerViewId$.pipe(
    switchMap(viewId => this.store.select(ViewSelectors.byId(viewId ?? 0))),
  );
  private autoSetHeight$ = toObservable(this.autoSetHeight);

  public ngOnInit() {
    this.viewDataService.refetchOnFieldsChange = this.refetchOnFieldsChange;
    this.watchCurrentFiltersRecordsCount();
    this.watchRefreshDataOnEmptyState();

    this.autoSetHeight$
      .pipe(
        switchMap(auto => (auto ? this.window.resize$.pipe(startWith(null)) : [])),
        this.untilDestroyed,
      )
      .subscribe(() => {
        const pageHeight = this.window.nativeWindow.innerHeight;
        const offset = this.selfElRef.nativeElement.getBoundingClientRect();
        this.height.set(pageHeight - offset.top);
      });

    this.dataSource$.pipe(distinctUntilChanged(), this.untilDestroyed).subscribe(dataSource => {
      this.viewFormService.setDefaults(dataSource);
      this.store.dispatch(
        ViewActions.fetchOne({
          payload: { dataSourceId: dataSource.id, viewId: this.viewId() },
        }),
      );
      this.viewDataService.routerViewId$.next(this.viewId());
    });

    this.currentView$.pipe(filterBoolean, this.untilDestroyed).subscribe(viewData => {
      const { view, snapshot } = viewData;
      const mergedView = this.viewDataService.mergeView(view, snapshot, this.dataSource());
      this.currentViewProvider$.next(mergedView);
      this.viewFormService.restoreFromView(this.dataSource(), mergedView);
      this.viewDataService.refreshCurrentFiltersAllRecordsCount();
      this.viewForm.markAsPristine();
      this.initialViewLoaded.set(true);
      this.isPredefinedView.set(mergedView.predefined);
      this.currentViewClean.set(view);
    });
  }

  private watchCurrentFiltersRecordsCount() {
    this.viewDataService.currentFiltersRecordsCount$
      .pipe(
        filter(({ data }) => !!data),
        this.untilDestroyed,
      )
      .subscribe(({ data, meta }) => {
        this.isLoaded.set(meta.isLoaded);
        this.isEmpty.set(meta.isLoaded && data!.meta.totalCount === 0);
        this.viewsRecordsTotalCount.set(data!.meta.totalCount ?? null);
      });
  }

  private watchRefreshDataOnEmptyState() {
    combineLatest([
      toObservable(this.isEmpty, { injector: this.injector }),
      toObservable(this.isLoaded, { injector: this.injector }),
    ])
      .pipe(
        map(([isEmpty, isLoaded]) => isEmpty && isLoaded),
        switchMap(isEmptyState => (isEmptyState ? this.dataSourceService$ : [])),
        switchMap((service: DataSourceService) => service.refreshData$),
        this.untilDestroyed,
      )
      .subscribe(() => {
        this.viewDataService.clearRecords();
        this.viewDataService.clearGroups();
        this.viewDataService.clearSwimlanes();
        this.viewDataService.clearLayoutCount();
        this.viewDataService.refreshCurrentFiltersAllRecordsCount();
      });
  }
}
